import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader2, ArrowLeft } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import api from '../../../../services/api';

const SERVICES = ['Hospice', 'Home Health'];
const STATUSES = ['New', 'Contacted', 'Qualified', 'Pending', 'Converted', 'Lost'];

const Field = ({ label, name, type = 'text', required = false, value, onChange, children }) => (
    <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
            {label}{required && <span className="text-red-500 ml-1">*</span>}
        </label>
        {children || (
            <input
                type={type}
                value={value}
                onChange={onChange}
                required={required}
                style={{ color: '#000000', backgroundColor: '#ffffff' }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-black bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
        )}
    </div>
);

export default function LeadForm() {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEdit = Boolean(id);

    const [form, setForm] = useState({
        name: '', phone: '', email: '', city: '',
        service_requested: '', diagnosis: '', insurance_type: '',
        referral_source_id: '', notes: '', assigned_staff_id: '', status: 'New',
    });
    const [referralSources, setReferralSources] = useState([]);
    const [users, setUsers] = useState([]);
    const [services, setServices] = useState([]);
    const [loading, setLoading] = useState(true); // Always start loading to fetch initial data
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                // Fetch sequentially to be safer against individual endpoint failures
                try {
                    const rsRes = await api.get('/referral-sources');
                    setReferralSources(rsRes.data || []);
                } catch (e) { console.error('RS load err', e); }

                try {
                    const usersRes = await api.get('/staff-list');
                    setUsers(usersRes.data || []);
                } catch (e) { console.error('Staff load err', e); }

                let servList = [];
                try {
                    // Match the per_page=100 from screenshot
                    const servRes = await api.get('/services?per_page=100');
                    servList = servRes.data?.data || servRes.data || [];
                    setServices(servList);
                } catch (e) { console.error('Serv load err', e); }

                if (isEdit) {
                    const leadRes = await api.get(`/leads/${id}`);
                    const l = leadRes.data;
                    setForm({
                        name: l.name || '', phone: l.phone || '', email: l.email || '',
                        city: l.city || '', service_requested: l.service_requested || '',
                        diagnosis: l.diagnosis || '', insurance_type: l.insurance_type || '',
                        referral_source_id: l.referral_source_id || '',
                        notes: l.notes || '', assigned_staff_id: l.assigned_staff_id || '',
                        status: l.status || 'New',
                    });
                } else if (!form.service_requested && servList.length > 0) {
                    // Set default service for new leads only if not already set
                    const firstTitle = servList[0].title?.en || servList[0].title || servList[0].name || '';
                    setForm(p => ({ ...p, service_requested: firstTitle }));
                }
            } catch (err) {
                toast.error('Failed to load form data');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [id, isEdit]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = { ...form };
            if (!payload.referral_source_id) delete payload.referral_source_id;
            if (!payload.assigned_staff_id) delete payload.assigned_staff_id;

            if (isEdit) {
                await api.put(`/leads/${id}`, payload);
                toast.success('Lead updated');
            } else {
                await api.post('/leads', payload);
                toast.success('Lead created');
            }
            setTimeout(() => navigate('/admin/leads'), 800);
        } catch (err) {
            const msg = err.response?.data?.message || 'Failed to save lead';
            toast.error(msg);
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="flex justify-center items-center min-h-screen"><Loader2 className="w-10 h-10 animate-spin text-blue-600" /></div>;

    return (
        <div>
            <Toaster position="top-right" richColors />
            <div className="max-w-2xl mx-auto">
                <div className="flex items-center gap-3 mb-6">
                    <button onClick={() => navigate('/admin/leads')} className="p-2 hover:bg-gray-200 rounded-lg"><ArrowLeft className="w-5 h-5" /></button>
                    <h2 className="text-2xl font-bold text-gray-900">{isEdit ? 'Edit Lead' : 'Add New Lead'}</h2>
                </div>

                <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 space-y-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <Field label="Full Name" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} required />
                        <Field label="Phone" value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} required />
                        <Field label="Email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} type="email" />
                        <Field label="City" value={form.city} onChange={e => setForm(p => ({ ...p, city: e.target.value }))} />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <Field label="Service Requested" required>
                            <select 
                                value={form.service_requested} 
                                onChange={e => setForm(p => ({ ...p, service_requested: e.target.value }))} 
                                style={{ color: '#000000', backgroundColor: '#ffffff', colorScheme: 'light' }}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-black bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
                            >
                                {services.map(s => {
                                    const title = s.title?.en || s.title || s.name;
                                    return <option key={s.id} value={title}>{title}</option>;
                                })}
                            </select>
                        </Field>
                        <Field label="Status">
                            <select 
                                value={form.status} 
                                onChange={e => setForm(p => ({ ...p, status: e.target.value }))} 
                                style={{ color: '#000000', backgroundColor: '#ffffff', colorScheme: 'light' }}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-black bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
                            >
                                {STATUSES.map(s => <option key={s}>{s}</option>)}
                            </select>
                        </Field>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <Field label="Diagnosis" value={form.diagnosis} onChange={e => setForm(p => ({ ...p, diagnosis: e.target.value }))} />
                        <Field label="Insurance Type" value={form.insurance_type} onChange={e => setForm(p => ({ ...p, insurance_type: e.target.value }))} />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <Field label="Referral Source">
                            <select 
                                value={form.referral_source_id} 
                                onChange={e => setForm(p => ({ ...p, referral_source_id: e.target.value }))} 
                                style={{ color: '#000000', backgroundColor: '#ffffff', colorScheme: 'light' }}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-black bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
                            >
                                <option value="">None</option>
                                {referralSources.map(rs => <option key={rs.id} value={rs.id}>{rs.facility_name}</option>)}
                            </select>
                        </Field>
                        <Field label="Assign to Staff">
                            <select 
                                value={form.assigned_staff_id} 
                                onChange={e => setForm(p => ({ ...p, assigned_staff_id: e.target.value }))} 
                                style={{ color: '#000000', backgroundColor: '#ffffff', colorScheme: 'light' }}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-black bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
                            >
                                <option value="">Unassigned</option>
                                {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                            </select>
                        </Field>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                        <textarea 
                            value={form.notes} 
                            onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} 
                            rows={4} 
                            style={{ color: '#000000', backgroundColor: '#ffffff' }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-black bg-white focus:outline-none focus:ring-2 focus:ring-blue-500" 
                        />
                    </div>

                    <div className="flex gap-3 pt-2">
                        <button type="submit" disabled={saving} className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition disabled:opacity-50">
                            {saving ? 'Saving…' : isEdit ? 'Update Lead' : 'Create Lead'}
                        </button>
                        <button type="button" onClick={() => navigate('/admin/leads')} className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">
                            Cancel
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
